// frontend/src/components/TraineeHistorySection.jsx
// 教育進捗ダッシュボード - 過去配置分析 - 育成された人 (被指導者) の過去配置
//
//   1. 直近の育成者一覧 (診療科 / 術式 / 指導日 / 育成者 (被指導者) / 指導者)
//   2. 特定術式の育成者一覧 (一覧から術式クリック or フィルタ指定で絞り込み)
//   3. 特定の人の直近育成一覧 (一覧から育成者クリック or フィルタ個人指定で絞り込み)
//
// データ: staff_assignments where pairing_mode='trainee'
//   - staff_id             : 被指導者 = 育成者 (= この行の主体)
//   - paired_with_staff_id : 指導者
//   - procedure_id, assignment_date

import { useEffect, useMemo, useState } from "react";
import { Search, AlertTriangle } from "lucide-react";
import { supabase } from "../supabase";

const PAGE_SIZE = 1000;

// 「育成者 (被指導者) 側」として扱うレコード:
//   - pairing_mode='trainee' が明示されたもの
//   - または pairing_mode が null で paired_with_staff_id が set + level_at_assignment が
//     独り立ち 未満 (非 '独り立ち'/'指導可') のもの
const SENIOR_LEVELS = new Set(["独り立ち", "指導可"]);
function recordLooksLikeTrainee(r) {
  if (r.pairing_mode === "trainee") return true;
  if (r.pairing_mode) return false;
  if (!r.paired_with_staff_id) return false;
  if (!r.level_at_assignment) return false;
  return !SENIOR_LEVELS.has(r.level_at_assignment);
}

async function fetchAssignmentsInPeriod(organizationId, fromDate, toDate) {
  const all = [];
  let page = 0;
  while (true) {
    const { data, error } = await supabase
      .from("staff_assignments")
      .select("id, staff_id, employee_id, procedure_id, procedure_name, department_name, role, assignment_date, pairing_mode, paired_with_staff_id, paired_with_name, level_at_assignment")
      .eq("organization_id", organizationId)
      .gte("assignment_date", fromDate)
      .lte("assignment_date", toDate)
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
    page += 1;
  }
  return all;
}

function resolveProcedure(record, procMap, procByName) {
  if (record.procedure_id && procMap[record.procedure_id]) {
    return procMap[record.procedure_id];
  }
  if (record.procedure_name) {
    const candidates = procByName[record.procedure_name] || [];
    if (record.department_name && candidates.length > 1) {
      const matched = candidates.find((p) => p.__deptName === record.department_name);
      if (matched) return matched;
    }
    return candidates[0] || null;
  }
  return null;
}

export default function TraineeHistorySection({
  organizationId,
  filters,
  departments,
  procedures,
  staffList,
  searchCount = 0,
}) {
  const [rawRecords, setRawRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 複合キー ({id, name, deptName}) で保持。 id が null でも name で drill-down 可能に。
  const [selectedProcedureKey, setSelectedProcedureKey] = useState(null);
  const [selectedTraineeId, setSelectedTraineeId] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    const run = async () => {
      if (!organizationId || searchCount === 0 || !filters.dateFrom || !filters.dateTo) return;
      setLoading(true);
      setError("");
      try {
        const fromDate = `${filters.dateFrom}-01`;
        const [ty, tm] = filters.dateTo.split("-").map(Number);
        const lastDay = new Date(ty, tm, 0).getDate();
        const toDate = `${filters.dateTo}-${String(lastDay).padStart(2, "0")}`;
        const data = await fetchAssignmentsInPeriod(organizationId, fromDate, toDate);
        setRawRecords(data);
        setSelectedProcedureKey(null);
        setSelectedTraineeId(null);
      } catch (e) {
        console.error(e);
        setError(e.message || "取得失敗");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [organizationId, searchCount]);

  const deptMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);
  const procMap = useMemo(() => Object.fromEntries(procedures.map((p) => [p.id, p])), [procedures]);
  const procByName = useMemo(() => {
    const m = {};
    procedures.forEach((p) => {
      const nm = p.name;
      if (!nm) return;
      const dept = departments.find((d) => d.id === p.department_id);
      const enriched = { ...p, __deptName: dept?.name || null };
      if (!m[nm]) m[nm] = [];
      m[nm].push(enriched);
    });
    return m;
  }, [procedures, departments]);
  const staffMap = useMemo(() => Object.fromEntries(staffList.map((s) => [s.id, s])), [staffList]);

  // 育成者候補のレコード
  const traineeRecords = useMemo(
    () => rawRecords.filter(recordLooksLikeTrainee),
    [rawRecords]
  );

  const diag = useMemo(() => {
    const total = rawRecords.length;
    const explicit = rawRecords.filter((r) => r.pairing_mode === "trainee").length;
    const inferred = rawRecords.filter((r) => recordLooksLikeTrainee(r) && r.pairing_mode !== "trainee").length;
    return { total, explicit, inferred };
  }, [rawRecords]);

  // フィルタ適用 (育成者側に staff 系フィルタをあてる)
  const filteredRecords = useMemo(() => {
    return traineeRecords.filter((r) => {
      const proc = resolveProcedure(r, procMap, procByName);
      const hasProc = !!proc;

      if (filters.deptIds.size > 0) {
        if (!hasProc || !filters.deptIds.has(proc.department_id)) return false;
      }
      if (filters.procedureIds.size > 0) {
        if (!hasProc || !filters.procedureIds.has(proc.id)) return false;
      }

      if (filters.levelIds.size > 0 && hasProc) {
        const candidates = new Set();
        if (proc.level_id) candidates.add(proc.level_id);
        (proc.required_roles || []).forEach((rr) => {
          if (rr.levelId) candidates.add(rr.levelId);
        });
        if (candidates.size > 0) {
          const hit = [...candidates].some((lid) => filters.levelIds.has(lid));
          if (!hit) return false;
        }
      }

      if (filters.requirements.size > 0 && hasProc) {
        const reqs = (proc.required_roles || []).map((rr) => rr.requirement || "required");
        const hit = reqs.some((req) => filters.requirements.has(req));
        if (!hit && reqs.length > 0) return false;
      }

      // 役割フィルタ
      if (filters.roles && filters.roles.size > 0 && r.role) {
        if (!filters.roles.has(r.role)) return false;
      }

      // 育成者 (= staff_id) 側のフィルタ
      const trainee = staffMap[r.staff_id];
      if (filters.years.size > 0) {
        if (!trainee || !filters.years.has(String(trainee.year))) return false;
      }
      if (filters.staffIds.size > 0 && !filters.staffIds.has(r.staff_id)) return false;

      return true;
    });
  }, [traineeRecords, procMap, staffMap, filters, procByName]);

  const procKeyFromRecord = (r) => {
    const proc = resolveProcedure(r, procMap, procByName);
    const deptName = (proc && deptMap[proc.department_id]?.name) || r.department_name || null;
    const name = proc?.name || r.procedure_name || null;
    return { id: proc?.id || null, name, deptName };
  };

  const procKeyMatches = (a, b) => {
    if (!a || !b) return false;
    if (a.id && b.id) return a.id === b.id;
    if (a.name && b.name) {
      return a.name === b.name && (a.deptName || "") === (b.deptName || "");
    }
    return false;
  };

  const rowsFromRecords = (recs) => {
    return recs.map((r) => {
      const procKey = procKeyFromRecord(r);
      const trainee = staffMap[r.staff_id];
      return {
        id: r.id,
        departmentName: procKey.deptName || "(不明)",
        procedureKey: procKey,
        procedureName: procKey.name || "(不明)",
        traineeId: trainee?.id || r.staff_id,
        traineeName: trainee
          ? `${trainee.last_name || ""} ${trainee.first_name || ""}`.trim()
          : "(不明)",
        traineeEmpId: trainee?.employee_id || r.employee_id || "",
        instructorId: r.paired_with_staff_id || null,
        instructorName: r.paired_with_name || "(未記録)",
        date: r.assignment_date,
      };
    });
  };

  const sortByDate = (rows, dir) => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      const cmp = (a.date || "").localeCompare(b.date || "");
      return dir === "asc" ? cmp : -cmp;
    });
    return sorted;
  };

  const mainRows = useMemo(
    () => sortByDate(rowsFromRecords(filteredRecords), sortDir),
    [filteredRecords, sortDir, procMap, staffMap, deptMap]
  );

  const effectiveProcedureKey = useMemo(() => {
    if (selectedProcedureKey) return selectedProcedureKey;
    if (filters.procedureIds.size === 1) {
      const pid = [...filters.procedureIds][0];
      const proc = procMap[pid];
      if (proc) {
        return {
          id: proc.id,
          name: proc.name,
          deptName: deptMap[proc.department_id]?.name || null,
        };
      }
    }
    return null;
  }, [selectedProcedureKey, filters.procedureIds, procMap, deptMap]);

  const effectiveTraineeId = useMemo(() => {
    if (selectedTraineeId) return selectedTraineeId;
    if (filters.staffIds.size === 1) return [...filters.staffIds][0];
    return null;
  }, [selectedTraineeId, filters.staffIds]);

  // 特定術式の育成者一覧 (育成回数順)
  const procedureTraineeSummary = useMemo(() => {
    if (!effectiveProcedureKey) return null;
    const scope = filteredRecords.filter((r) => procKeyMatches(procKeyFromRecord(r), effectiveProcedureKey));
    const map = new Map();
    scope.forEach((r) => {
      const t = staffMap[r.staff_id];
      const id = t?.id || r.staff_id;
      const name = t ? `${t.last_name || ""} ${t.first_name || ""}`.trim() : "(不明)";
      const empId = t?.employee_id || "";
      const cur = map.get(id) || { id, name, empId, count: 0, latest: "" };
      cur.count += 1;
      if (r.assignment_date > cur.latest) cur.latest = r.assignment_date;
      map.set(id, cur);
    });
    return [...map.values()].sort((a, b) => b.count - a.count || b.latest.localeCompare(a.latest));
  }, [effectiveProcedureKey, filteredRecords, staffMap, procMap, procByName, deptMap]);

  const personRecentRows = useMemo(() => {
    if (!effectiveTraineeId) return null;
    const scope = filteredRecords.filter((r) => r.staff_id === effectiveTraineeId);
    return sortByDate(rowsFromRecords(scope), "desc");
  }, [effectiveTraineeId, filteredRecords, procMap, staffMap, deptMap]);

  if (searchCount === 0) {
    return (
      <div style={styles.placeholder}>
        <Search size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
        上部で条件を設定し、「検索を実行」を押すと集計が表示されます。
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.loadingBig}>
        <div style={styles.spinner} />
        <div style={{ fontSize: 13, color: "#1d4ed8", fontWeight: 600 }}>データを読み込み中...</div>
      </div>
    );
  }

  return (
    <div>
      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.diagBanner}>
        <span>期間内の staff_assignments 総数: <strong>{diag.total}</strong></span>
        <span>うち pairing_mode=&apos;trainee&apos;: <strong>{diag.explicit}</strong></span>
        <span>pairing_mode 空だが下位レベルで推定: <strong>{diag.inferred}</strong></span>
        {diag.total > 0 && diag.explicit + diag.inferred === 0 && (
          <span style={{ color: "#b91c1c", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <AlertTriangle size={13} />
            育成者判定できる行がありません。 CSV インポート時に「指導役割 (pairing_mode)」列をマップしましたか？
          </span>
        )}
      </div>

      {/* ===== 1. 直近の育成者一覧 ===== */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>直近の育成者一覧</h3>
          <span style={styles.count}>{mainRows.length} 件</span>
        </div>
        {mainRows.length === 0 ? (
          <div style={styles.empty}>該当する育成記録がありません</div>
        ) : (
          <div style={{ overflow: "auto", maxHeight: 420 }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>診療科</th>
                  <th style={styles.th}>術式</th>
                  <th
                    style={{ ...styles.th, cursor: "pointer", userSelect: "none" }}
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  >
                    指導日 {sortDir === "asc" ? "▲" : "▼"}
                  </th>
                  <th style={styles.th}>育成者 (被指導)</th>
                  <th style={styles.th}>指導者</th>
                </tr>
              </thead>
              <tbody>
                {mainRows.slice(0, 500).map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>{row.departmentName}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => setSelectedProcedureKey(row.procedureKey)}
                        style={styles.linkBtn}
                        title="この術式に絞り込む"
                      >
                        {row.procedureName}
                      </button>
                    </td>
                    <td style={styles.td}>{row.date}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => setSelectedTraineeId(row.traineeId)}
                        style={styles.linkBtn}
                        title="この育成者に絞り込む"
                      >
                        {row.traineeName}
                      </button>
                    </td>
                    <td style={styles.td}>{row.instructorName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {mainRows.length > 500 && (
              <div style={styles.limitNote}>
                件数が多いため先頭 500 件のみ表示 (全 {mainRows.length} 件)
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== 2. 特定術式の育成者一覧 ===== */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            特定術式の育成者一覧
            {effectiveProcedureKey && (
              <span style={styles.subBadge}>— {effectiveProcedureKey.name || "(名称不明)"}</span>
            )}
          </h3>
          {selectedProcedureKey && (
            <button onClick={() => setSelectedProcedureKey(null)} style={styles.clearBtn}>選択解除</button>
          )}
        </div>
        {!effectiveProcedureKey ? (
          <div style={styles.hint}>
            上の一覧から術式をクリック、または上部フィルタで術式を 1 件に絞ると表示されます。
          </div>
        ) : procedureTraineeSummary.length === 0 ? (
          <div style={styles.empty}>該当する育成記録がありません</div>
        ) : (
          <div style={{ overflow: "auto", maxHeight: 420 }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>育成者</th>
                  <th style={styles.th}>社員番号</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>育成回数</th>
                  <th style={styles.th}>直近日</th>
                </tr>
              </thead>
              <tbody>
                {procedureTraineeSummary.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <button
                        onClick={() => setSelectedTraineeId(row.id)}
                        style={styles.linkBtn}
                      >{row.name}</button>
                    </td>
                    <td style={styles.td}>{row.empId}</td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: 700 }}>{row.count}</td>
                    <td style={styles.td}>{row.latest}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== 3. 特定の人の直近育成一覧 ===== */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h3 style={styles.cardTitle}>
            特定の人の直近育成一覧
            {effectiveTraineeId && (
              <span style={styles.subBadge}>
                — {(() => {
                  const s = staffMap[effectiveTraineeId];
                  return s ? `${s.last_name || ""} ${s.first_name || ""}`.trim() : effectiveTraineeId;
                })()}
              </span>
            )}
          </h3>
          {selectedTraineeId && (
            <button onClick={() => setSelectedTraineeId(null)} style={styles.clearBtn}>選択解除</button>
          )}
        </div>
        {!effectiveTraineeId ? (
          <div style={styles.hint}>
            上の一覧から育成者をクリック、または上部フィルタの「個人」を 1 件に絞ると表示されます。
          </div>
        ) : personRecentRows.length === 0 ? (
          <div style={styles.empty}>この育成者の記録がありません</div>
        ) : (
          <div style={{ overflow: "auto", maxHeight: 420 }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>診療科</th>
                  <th style={styles.th}>術式</th>
                  <th style={styles.th}>指導日</th>
                  <th style={styles.th}>指導者</th>
                </tr>
              </thead>
              <tbody>
                {personRecentRows.slice(0, 300).map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>{row.departmentName}</td>
                    <td style={styles.td}>{row.procedureName}</td>
                    <td style={styles.td}>{row.date}</td>
                    <td style={styles.td}>{row.instructorName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {personRecentRows.length > 300 && (
              <div style={styles.limitNote}>
                件数が多いため先頭 300 件のみ表示 (全 {personRecentRows.length} 件)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  placeholder: {
    padding: 50, textAlign: "center", color: "#64748b", fontSize: 13,
    background: "#f8fafc", borderRadius: 8, border: "1px dashed #cbd5e1",
  },
  loadingBig: {
    padding: 40, display: "flex", flexDirection: "column", alignItems: "center",
    gap: 12, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10,
  },
  spinner: {
    width: 32, height: 32, borderRadius: "50%",
    border: "3px solid #bfdbfe", borderTopColor: "#1d4ed8",
    animation: "spin 0.8s linear infinite",
  },
  diagBanner: {
    display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center",
    fontSize: 11, color: "#334155",
    background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8,
    padding: "8px 12px", marginBottom: 10,
  },
  card: {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: 16, marginBottom: 14,
  },
  cardHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 10,
  },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#0f172a", margin: 0 },
  subBadge: { fontSize: 12, color: "#1d4ed8", marginLeft: 8, fontWeight: 600 },
  count: { fontSize: 11, color: "#64748b" },
  clearBtn: {
    fontSize: 11, padding: "4px 10px",
    background: "#eff6ff", color: "#1d4ed8",
    border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "8px 10px", textAlign: "left",
    borderBottom: "2px solid #e2e8f0",
    fontSize: 11, color: "#475569", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.5px",
    background: "#f8fafc",
  },
  td: {
    padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#0f172a",
  },
  linkBtn: {
    background: "none", border: "none", color: "#1d4ed8",
    cursor: "pointer", padding: 0, fontSize: 13, fontWeight: 600,
    textAlign: "left",
  },
  empty: {
    padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 12,
    background: "#f8fafc", borderRadius: 8, border: "1px dashed #cbd5e1",
  },
  hint: {
    padding: 20, textAlign: "center", color: "#64748b", fontSize: 12,
    background: "#f8fafc", borderRadius: 8,
  },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 10,
  },
  loadingBox: {
    background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8",
    padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 10,
  },
  limitNote: {
    marginTop: 8, fontSize: 11, color: "#94a3b8", textAlign: "center",
  },
};
